import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';

// CSS stílusok a leírás tartalmának megfelelő megjelenítéséhez
const descriptionStyles = `
  .discount-description-full {
    margin-top: 1rem;
    margin-bottom: 1.5rem;
  }
  .discount-description-full h1, .discount-description-full h2, .discount-description-full h3 {
    margin-top: 1em;
    margin-bottom: 0.5em;
    font-weight: 600;
  }
  .discount-description-full h1 {
    font-size: 1.5rem;
  }
  .discount-description-full h2 {
    font-size: 1.25rem;
  }
  .discount-description-full h3 {
    font-size: 1.125rem;
  }
  .discount-description-full p {
    margin-bottom: 1em;
  }
  .discount-description-full ul, .discount-description-full ol {
    margin-left: 1.5em;
    margin-bottom: 1em;
  }
  .discount-description-full li {
    margin-bottom: 0.5em;
  }
  .discount-description-full a {
    color: #4f46e5;
    text-decoration: underline;
  }
  .discount-description-full img {
    max-width: 100%;
    height: auto;
    margin: 1em 0;
  }
  .discount-description-full blockquote {
    border-left: 4px solid #e5e7eb;
    padding-left: 1em;
    margin-left: 0;
    margin-right: 0;
    font-style: italic;
  }
  .discount-description-full table {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
  }
  .discount-description-full table td, .discount-description-full table th {
    border: 1px solid #e5e7eb;
    padding: 0.5em;
  }
  .discount-description-full table th {
    background-color: #f9fafb;
  }
`;

// Dinamikus ikon komponens
const DynamicIcon = ({ iconName, ...props }) => {
  const Icon = Icons[iconName];
  return Icon ? <Icon {...props} /> : null;
};

export default function DiscountDetails({ discountId, onBack }) {
  const { t } = useTranslation();
  const [discount, setDiscount] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCouponModal, setShowCouponModal] = useState(false);

  useEffect(() => {
    const fetchDiscountDetails = async () => {
      try {
        setLoading(true);
        
        // Kedvezmény adatainak lekérése
        const discountDoc = await getDoc(doc(db, 'discounts', discountId));
        
        if (!discountDoc.exists()) {
          setError('A kedvezmény nem található');
          setLoading(false);
          return;
        }
        
        const discountData = {
          id: discountDoc.id,
          ...discountDoc.data()
        };
        
        setDiscount(discountData);
        
        // Kategória adatainak lekérése
        if (discountData.categoryId) {
          const categoryDoc = await getDoc(doc(db, 'discountCategories', discountData.categoryId));
          if (categoryDoc.exists()) {
            setCategory({
              id: categoryDoc.id,
              ...categoryDoc.data()
            });
          }
        }
        
      } catch (err) {
        console.error('Hiba a kedvezmény betöltésekor:', err);
        setError('Hiba történt a kedvezmény betöltésekor');
      } finally {
        setLoading(false);
      }
    };
    
    if (discountId) {
      fetchDiscountDetails();
    }
  }, [discountId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Icons.Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Icons.AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-lg font-medium text-gray-900">{error}</h3>
        <div className="mt-6">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-3 px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-md transition-all duration-200"
          >
            <Icons.ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
            <span>Vissza a kedvezményekhez</span>
          </button>
        </div>
      </div>
    );
  }

  if (!discount) {
    return (
      <div className="text-center py-12">
        <Icons.Package className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-lg font-medium text-gray-900">Kedvezmény nem található</h3>
        <div className="mt-6">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-3 px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-md transition-all duration-200"
          >
            <Icons.ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
            <span>Vissza a kedvezményekhez</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white w-full">
      <style>{descriptionStyles}</style>
      
      {/* Kedvezmény fejléc */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
        {discount.logoUrl && (
          <div className="relative h-80 sm:h-96 md:h-[28rem] overflow-hidden">
            <img
              src={discount.logoUrl}
              alt={discount.title}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-transparent"></div>
            
            {/* Hero tartalom */}
            <div className="absolute inset-x-0 top-0 p-6 sm:p-8 md:p-10 text-white z-10">
              <button
                onClick={onBack}
                className="inline-flex items-center gap-3 px-5 py-2.5 border border-white/40 text-sm font-medium rounded-lg text-white bg-black/40 hover:bg-black/50 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mb-6 shadow-lg transition-all duration-200"
              >
                <Icons.ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
                <span>Vissza a kedvezményekhez</span>
              </button>
              
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 drop-shadow-md">
                {discount.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {category && (
                  <div className="flex items-center">
                    <DynamicIcon 
                      iconName={category.icon} 
                      className="h-5 w-5 mr-2 text-white" 
                    />
                    <span className="text-sm font-medium text-white/90">
                      {category.name}
                    </span>
                  </div>
                )}
                
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-500/80 text-white backdrop-blur-sm">
                  <Icons.Percent className="h-4 w-4 mr-1" />
                  {discount.percentage}% kedvezmény
                </div>
                
                {discount.couponCode && (
                  <button
                    onClick={() => setShowCouponModal(true)}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-500/80 text-white hover:bg-indigo-600/80 backdrop-blur-sm"
                  >
                    <Icons.Ticket className="h-4 w-4 mr-1" />
                    Kuponkód
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div className="p-6 sm:p-8 relative">
          {/* Kedvezmény részletek */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Részletes leírás</h2>
              <div 
                className="prose prose-indigo max-w-none discount-description-full" 
                dangerouslySetInnerHTML={{ __html: discount.description }} 
              />
            </div>
            
            <div className="space-y-6">
              {/* Információs kártya - csak akkor jelenik meg, ha van legalább egy információ */}
              {(discount.validUntil || discount.usageLimit || discount.terms) && (
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Kedvezmény információk</h3>
                  
                  <div className="space-y-4">
                    {discount.validUntil && (
                      <div>
                        <div className="flex items-center text-sm text-gray-500 mb-1">
                          <Icons.Calendar className="h-4 w-4 mr-2" />
                          <span>Érvényesség</span>
                        </div>
                        <div className="font-medium">
                          {new Date(discount.validUntil.toDate()).toLocaleDateString('hu-HU')}
                        </div>
                      </div>
                    )}
                    
                    {discount.usageLimit && (
                      <div>
                        <div className="flex items-center text-sm text-gray-500 mb-1">
                          <Icons.Users className="h-4 w-4 mr-2" />
                          <span>Felhasználási limit</span>
                        </div>
                        <div className="font-medium">
                          {discount.usageLimit} felhasználás
                        </div>
                      </div>
                    )}
                    
                    {discount.terms && (
                      <div>
                        <div className="flex items-center text-sm text-gray-500 mb-1">
                          <Icons.FileText className="h-4 w-4 mr-2" />
                          <span>Felhasználási feltételek</span>
                        </div>
                        <div className="text-sm">
                          {discount.terms}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Akció gomb - csak akkor jelenik meg, ha van URL */}
              {discount.link && (
                <a
                  href={discount.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center px-4 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Kedvezmény igénybevétele
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Kuponkód Modal */}
      {showCouponModal && discount.couponCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowCouponModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
            >
              <Icons.X className="h-5 w-5" />
            </button>
            
            <div className="text-center">
              <Icons.Ticket className="mx-auto h-12 w-12 text-indigo-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {discount.title}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Használd ezt a kuponkódot a kedvezmény érvényesítéséhez
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-2xl font-mono font-bold text-indigo-600 select-all">
                  {discount.couponCode}
                </p>
              </div>
              
              <button
                onClick={() => {
                  navigator.clipboard.writeText(discount.couponCode);
                  toast.success('Kuponkód másolva!');
                }}
                className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <Icons.Copy className="h-4 w-4 mr-2" />
                Kuponkód másolása
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 